/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Download,
  Check,
  Cross,
  User,
  Building,
  UserPlus,
  Send,
  PauseCircle,
  FileX,
  Refresh,
  CircleCheck,
  CircleCross,
  UserCheck,
  InfoCircle,
  Mail,
  Phone,
  ChevronRight,
} from '@saleshandy/icons';
import { Spinner } from 'react-bootstrap';
import { SkeletonLoading } from '@saleshandy/design-system';
import Button from '../../../../shared/design-system/components/atoms/button';
import { RequestStatus } from '../../../../shared/enums/request-status';
import TanStackTable from '../../../../shared/design-system/ui/tanstack-table';
import InfiniteScrollCustom from '../../../../shared/design-system/components/atoms/infinite-scroll-custom';
import { Routes } from '../../../../shared/routes';
import {
  EnrichmentStatus,
  EnrichmentType,
  EnrichmentComponent,
} from '../../enums/csv-enrichment.enums';
import {
  EnrichmentJob,
  EnrichedRecord,
} from '../../types/csv-enrichment.types';
import NoSearchResult from '../../../v2-lead/components/no-search-result';
import NoSearchResultIcon from '../../../../shared/components/images/no-search-result-icon';
import { buildPeopleStatusColumns } from '../../helpers/people-enrichment-status-columns';
import { buildCompanyStatusColumns } from '../../helpers/company-enrichment-status-columns';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../../shared/design-system/components/overlay';
import THeadLoader from '../../../../shared/design-system/ui/tanstack-table/components/thead-loader';
import TBodyLoader from '../../../../shared/design-system/ui/tanstack-table/components/tbody-loader';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import { getComponentDisplayLabel } from '../../helpers/csv-enrichment.helpers';
import { useCanShowDataExport } from '../../../../shared/utils/data-export-permission';

const TYPE_CHIP: Record<string, string> = {
  people: 'People',
  company: 'Company',
};

type EnrichmentStatusPageProps = {
  job: EnrichmentJob | null;
  recordsJob: EnrichmentJob | null;
  records: EnrichedRecord[];
  recordColumns: string[];
  recordsRequestStatus: RequestStatus;
  recordsHasMore: boolean;
  recordsIsLoadingMore: boolean;
  onLoadMore: () => void;
  onExport: (id: string) => void | Promise<void>;
  onAddToProspects: (id: string) => void;
  onAddToSequence: (id: string) => void;
  failedReport: (id: string) => void | Promise<void>;
  onCancel: () => void;
};

const EnrichmentStatusPage: React.FC<EnrichmentStatusPageProps> = ({
  job,
  recordsJob,
  records,
  recordColumns,
  recordsRequestStatus,
  recordsHasMore,
  recordsIsLoadingMore,
  onLoadMore,
  onExport,
  onAddToProspects,
  onAddToSequence,
  failedReport,
  onCancel,
}) => {
  const history = useHistory();
  const canDisplayDataExport = useCanShowDataExport();

  const [isNotEnrichedOpen, setIsNotEnrichedOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isFailedReportLoading, setIsFailedReportLoading] = useState(false);

  const isProcessing = recordsJob?.status === EnrichmentStatus.PROCESSING;
  const isQueued = recordsJob?.status === EnrichmentStatus.QUEUED;
  const isCompleted = recordsJob?.status === EnrichmentStatus.COMPLETED;
  const isFailed = recordsJob?.status === EnrichmentStatus.FAILED;
  const isCancelled = recordsJob?.status === EnrichmentStatus.CANCELLED;
  const isLoading =
    recordsRequestStatus === RequestStatus.Pending ||
    recordsRequestStatus === RequestStatus.Ideal;
  const isRecordsPending = recordsRequestStatus === RequestStatus.Pending;

  const totalRecords = recordsJob?.totalRecords ?? job?.totalRecords ?? 0;
  const processedCount =
    recordsJob?.enrichedRecords ?? job?.enrichedRecords ?? 0;
  const progress = recordsJob?.progress ?? job?.progress ?? 0;
  const foundCount = processedCount;
  const notFoundCount = recordsJob?.failedRecords;
  const failedRecords =
    recordsJob?.failedRecords ||
    recordsJob?.totalRecords - recordsJob?.enrichedRecords;
  const hasFailedRecords = failedRecords > 0;

  const areProspectsCreated = recordsJob?.areProspectsCreated;
  const disableProspectAndSequence =
    (recordsJob?.status === EnrichmentStatus.COMPLETED &&
      records.length === 0) ||
    recordsJob?.enrichedRecords === 0 ||
    recordsJob?.status === EnrichmentStatus.FAILED;

  const handleBackToList = () => {
    history.push(Routes.CSV_Enrichment);
  };

  const handleExport = async () => {
    if (processedCount === 0 || isExportLoading) return;
    setIsExportLoading(true);
    try {
      await onExport(recordsJob?.jobId);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleFailedReport = async () => {
    if (!hasFailedRecords || isFailedReportLoading) return;
    setIsFailedReportLoading(true);
    try {
      await failedReport(recordsJob?.jobId);
    } finally {
      setIsFailedReportLoading(false);
    }
  };

  const isEmpty = !isLoading && records.length === 0;

  const enrichmentColumnType = useMemo(
    () =>
      recordsJob?.type ??
      job?.type ??
      recordsJob?.component ??
      job?.component ??
      null,
    [recordsJob?.type, recordsJob?.component, job?.type, job?.component],
  );

  const orderedRecordColumns = useMemo(() => {
    const cols = recordColumns || [];

    const prioritize = (priority: string[]) => {
      const set = new Set(cols);
      const picked: string[] = [];
      priority.forEach((k) => {
        if (set.has(k)) picked.push(k);
      });
      const rest = cols.filter((k) => !picked.includes(k));
      return [...picked, ...rest];
    };

    if (enrichmentColumnType === EnrichmentType.COMPANY) {
      return prioritize([
        'company_name',
        'company_emails',
        'company_email',
        'social_urls',
        'socials',
        'hq_location',
        'company_hq_location',
        'location',
      ]);
    }

    return prioritize([
      'full_name',
      'emails',
      'phones',
      'job_title',
      'location',
      'social_link',
    ]);
  }, [recordColumns, enrichmentColumnType]);

  const pinnedFirstColumn = useMemo(
    () => (orderedRecordColumns?.[0] ? [orderedRecordColumns[0]] : []),
    [orderedRecordColumns],
  );

  const columnDefs = useMemo(() => {
    if (enrichmentColumnType === EnrichmentType.COMPANY) {
      return buildCompanyStatusColumns(orderedRecordColumns) as any[];
    }
    return buildPeopleStatusColumns(orderedRecordColumns) as any[];
  }, [orderedRecordColumns, enrichmentColumnType]);

  useEffect(() => {
    if (!isNotEnrichedOpen) return undefined;
    const handleScroll = () => setIsNotEnrichedOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isNotEnrichedOpen]);

  const getStatusIconClass = () => {
    if (isFailed) return 'bs-csv-enrichment__status-icon--failed';
    if (isCompleted) return 'bs-csv-enrichment__status-icon--completed';
    if (isCancelled) return 'bs-csv-enrichment__status-icon--cancelled';
    return 'bs-csv-enrichment__status-icon--processing';
  };

  const getStatusLabel = () => {
    if (isFailed) return 'Enrichment Failed';
    if (isCompleted) return 'Enrichment Completed';
    if (isCancelled) return 'Enrichment Cancelled';
    if (isQueued) return 'Queued';
    if (isProcessing) return 'Enriched - Preparing your file';
    if (!recordsJob) return 'Loading...';
    return recordsJob.status;
  };

  const renderStatusIcon = () => {
    if (isCompleted) return <CircleCheck width={20} height={20} />;
    if (isCancelled) return <CircleCross width={20} height={20} />;
    if (isFailed) return <Cross width={20} height={20} />;
    return <Refresh width={20} height={20} />;
  };

  const renderEnrichedRecordsBlock = () => {
    if (isLoading && columnDefs.length === 0) {
      return (
        <div className="bs-csv-enrichment__records-table">
          <THeadLoader />
          <TBodyLoader />
        </div>
      );
    }
    if (!isLoading && isQueued && records?.length === 0) {
      return (
        <div className="csv-enrichment-no-record">
          <NoSearchResult
            description="Your records are being processed. Please wait."
            title="Fetching records"
          >
            <NoSearchResultIcon />
          </NoSearchResult>
        </div>
      );
    }
    if (isEmpty) {
      return (
        <div className="csv-enrichment-no-record">
          <NoSearchResult
            description={
              totalRecords
                ? `We processed your file but couldn't find matches for any of the ${totalRecords} records.`
                : `The enrichment record which you are looking for is not found.`
            }
            title={
              processedCount === 0
                ? `No Enriched Record Found`
                : notFoundCount === 0
                ? `No Failed Record Found`
                : `No record found`
            }
          >
            <NoSearchResultIcon />
          </NoSearchResult>
        </div>
      );
    }
    return (
      <div
        id="enrichment-records-scroll"
        className="bs-csv-enrichment__records-scroll"
      >
        <InfiniteScrollCustom
          wrapperId="enrichment-records-scroll-inner"
          wrapperStyle={{}}
          dataLength={records.length}
          next={onLoadMore}
          hasMore={recordsHasMore}
          endMessage={
            !recordsHasMore && records.length > 25 ? (
              <div className="end-list-divider">
                <span>The End</span>
              </div>
            ) : null
          }
          loader={
            recordsIsLoadingMore ? (
              <div className="bs-csv-enrichment__scroll-loader">
                <Spinner className="spinner--blue" animation="border" />
              </div>
            ) : null
          }
          scrollableTarget="enrichment-records-scroll"
        >
          <TanStackTable
            columns={columnDefs}
            rows={records}
            isLoading={isLoading}
            syncLayoutOnContainerResize
            enableSorting={false}
            enableRowSelection={false}
            enableColumnResizing
            pinnedColumns={pinnedFirstColumn}
            defaultPinnedColumns={pinnedFirstColumn}
            immovableColumns={pinnedFirstColumn}
            tableClassName="bs-csv-enrichment__records-table"
          />
        </InfiniteScrollCustom>
      </div>
    );
  };

  const meta = recordsJob ?? job;
  const typeChipKey = String(meta?.type ?? meta?.component ?? '')
    .toLowerCase()
    .trim();
  const typeChip = TYPE_CHIP[typeChipKey] ?? '';
  const revealChip = getComponentDisplayLabel(meta?.revealType);
  const revealChipKey = String(meta?.revealType ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  const renderTypeChipIcon = () => {
    if (typeChipKey === EnrichmentType.PEOPLE) return <User width={14} />;
    if (typeChipKey === EnrichmentType.COMPANY) return <Building width={14} />;
    return null;
  };

  const renderRevealChipIcon = () => {
    // Requirement:
    // - email → Email icon
    // - email + phone → Phone icon
    if (!revealChipKey) return null;
    const isPhoneReveal =
      revealChipKey === EnrichmentComponent.PHONE_EMAIL ||
      revealChipKey.includes('phone');
    return isPhoneReveal ? <Phone width={14} /> : <Mail width={14} />;
  };

  const renderStatusCardContent = () => {
    if (isRecordsPending) {
      return (
        <>
          <div className="bs-csv-enrichment__status-card-left">
            <SkeletonLoading width={40} height={40} borderRadius={8} />
            <div className="bs-csv-enrichment__status-card-info">
              <SkeletonLoading width={200} height={16} />
              <SkeletonLoading width={160} height={12} />
            </div>
          </div>

          <div className="bs-csv-enrichment__status-card-stats">
            <div className="bs-csv-enrichment__status-stat">
              <SkeletonLoading width={64} height={10} />
              <SkeletonLoading width={48} height={22} />
            </div>
            <span className="bs-csv-enrichment__status-divider" />
            <div className="bs-csv-enrichment__status-stat">
              <SkeletonLoading width={40} height={10} />
              <SkeletonLoading width={48} height={22} />
            </div>
            <span className="bs-csv-enrichment__status-divider" />
            <div className="bs-csv-enrichment__status-stat">
              <SkeletonLoading width={56} height={10} />
              <SkeletonLoading width={48} height={22} />
            </div>
          </div>

          <span className="bs-csv-enrichment__status-divider" />

          <div className="bs-csv-enrichment__status-card-actions">
            <SkeletonLoading width={200} height={36} borderRadius={4} />
            <span
              className="bs-csv-enrichment__status-divider"
              style={{ margin: '0 12px' }}
            />
            <SkeletonLoading width={96} height={36} />
          </div>
        </>
      );
    }

    return (
      <>
        <div className="bs-csv-enrichment__status-card-left">
          <div
            className={`bs-csv-enrichment__status-icon ${getStatusIconClass()}`}
          >
            {renderStatusIcon()}
          </div>
          <div className="bs-csv-enrichment__status-card-info">
            <span className="title">{getStatusLabel()}</span>
            <div className="bs-csv-enrichment__status-subtitle-truncate">
              <TruncatedTextWithAutoTooltip
                className="subtitle"
                text={recordsJob?.fileName || 'contacts.csv'}
              />
            </div>
          </div>
        </div>

        <div className="bs-csv-enrichment__status-card-stats">
          <div className="bs-csv-enrichment__status-stat bs-csv-enrichment__status-stat--processed">
            <span className="bs-csv-enrichment__status-stat-label">
              TOTAL RECORDS
            </span>
            <span className="bs-csv-enrichment__status-stat-val">
              {totalRecords.toLocaleString()}
            </span>
          </div>
          <span className="bs-csv-enrichment__status-divider" />
          <div className="bs-csv-enrichment__status-stat bs-csv-enrichment__status-stat--found">
            <span className="bs-csv-enrichment__status-stat-label">
              ENRICHED
            </span>
            <div className="bs-csv-enrichment__status-stat-val">
              <Check width={16} height={16} />
              <span>{foundCount?.toLocaleString()}</span>
            </div>
          </div>
          <span className="bs-csv-enrichment__status-divider" />
          <div className="bs-csv-enrichment__status-stat bs-csv-enrichment__status-stat--not-found">
            <span className="bs-csv-enrichment__status-stat-label">
              NOT ENRICHED
              {recordsJob?.failedRecords > 0 && (
                <OverlayPopover
                  placement={Placement.Bottom}
                  trigger={TriggerType.Click}
                  show={isNotEnrichedOpen}
                  onToggle={setIsNotEnrichedOpen}
                  rootClose
                  content={
                    <div className="bs-csv-enrichment__not-enriched-popover">
                      {recordsJob?.notFoundRecords > 0 && (
                        <div className="bs-csv-enrichment__not-enriched-popover-row">
                          <span className="regular-2">Not Found Records</span>
                          <span className="regular-2">
                            {recordsJob?.notFoundRecords.toLocaleString() ?? 0}
                          </span>
                        </div>
                      )}
                      {recordsJob?.duplicateRecords > 0 && (
                        <div className="bs-csv-enrichment__not-enriched-popover-row">
                          <span className="regular-2">Duplicate Records</span>
                          <span className="regular-2">
                            {recordsJob?.duplicateRecords.toLocaleString() ?? 0}
                          </span>
                        </div>
                      )}
                      {recordsJob?.invalidRecords > 0 && (
                        <div className="bs-csv-enrichment__not-enriched-popover-row">
                          <span className="regular-2">Invalid Records</span>
                          <span className="regular-2">
                            {recordsJob?.invalidRecords.toLocaleString() ?? 0}
                          </span>
                        </div>
                      )}
                    </div>
                  }
                  className="not-enriched-popover"
                >
                  <button
                    type="button"
                    className={`bs-csv-enrichment__not-enriched-info-btn${
                      isNotEnrichedOpen
                        ? ' bs-csv-enrichment__not-enriched-info-btn--active'
                        : ''
                    }`}
                  >
                    <InfoCircle width={16} height={16} />
                  </button>
                </OverlayPopover>
              )}
            </span>

            <div className="bs-csv-enrichment__status-stat-val">
              <Cross width={16} height={16} />
              <span>{notFoundCount?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <span className="bs-csv-enrichment__status-divider" />

        <div className="bs-csv-enrichment__status-card-actions">
          {(isProcessing || isQueued) && (
            <OverlayTooltip
              text="Cancelling will stop the enrichment, but records already in the queue will still be processed and you may get charged"
              placement={Placement.Bottom}
            >
              <Button
                variant={Button.Variant.Outlined}
                theme={Button.Theme.Solid}
                onClick={onCancel}
                className="back-to-list-btn"
              >
                <PauseCircle width={16} height={16} />
                <span>Cancel</span>
              </Button>
            </OverlayTooltip>
          )}

          {!isProcessing && !isQueued && recordsJob && (
            <>
              {recordsJob?.type === EnrichmentType.PEOPLE && (
                <>
                  <OverlayTooltip
                    placement={Placement.Bottom}
                    text={
                      areProspectsCreated
                        ? 'Prospect Saved'
                        : 'Save as Prospect'
                    }
                  >
                    <Button
                      onClick={() =>
                        !areProspectsCreated &&
                        onAddToProspects(recordsJob?.jobId)
                      }
                      className="action-btn"
                      disabled={
                        disableProspectAndSequence || areProspectsCreated
                      }
                    >
                      {areProspectsCreated ? (
                        <UserCheck width={18} height={18} />
                      ) : (
                        <UserPlus width={18} height={18} />
                      )}
                    </Button>
                  </OverlayTooltip>
                  <OverlayTooltip
                    placement={Placement.Bottom}
                    text="Add to Sequence"
                  >
                    <Button
                      onClick={() => onAddToSequence(recordsJob?.jobId)}
                      className="action-btn"
                      disabled={disableProspectAndSequence}
                    >
                      <Send width={18} height={18} />
                    </Button>
                  </OverlayTooltip>
                </>
              )}
              {canDisplayDataExport && (
                <>
                  <OverlayTooltip
                    placement={Placement.Bottom}
                    text={
                      processedCount === 0
                        ? 'No Enriched Records'
                        : `Export Enriched Report`
                    }
                  >
                    <Button
                      onClick={handleExport}
                      className="action-btn"
                      disabled={processedCount === 0 || isExportLoading}
                    >
                      {isExportLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Download width={18} height={18} />
                      )}
                    </Button>
                  </OverlayTooltip>
                  <OverlayTooltip
                    placement={Placement.Bottom}
                    text={
                      hasFailedRecords
                        ? 'Export Failed Report'
                        : 'No Failed Records'
                    }
                  >
                    <Button
                      onClick={handleFailedReport}
                      className="action-btn"
                      disabled={!hasFailedRecords || isFailedReportLoading}
                    >
                      {isFailedReportLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FileX width={18} height={18} />
                      )}
                    </Button>
                  </OverlayTooltip>
                </>
              )}
            </>
          )}

          <span
            className="bs-csv-enrichment__status-divider"
            style={{ margin: '0 12px' }}
          />

          <Button
            variant={Button.Variant.Outlined}
            theme={Button.Theme.Solid}
            onClick={handleBackToList}
            className="back-to-list-btn"
          >
            Back to list
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="bs-csv-enrichment__status-page">
      {/* Status card */}
      <div className="bs-csv-enrichment__status-card">
        <div className="bs-csv-enrichment__status-card-content">
          {renderStatusCardContent()}
        </div>

        {(isProcessing || isQueued) && (
          <div className="bs-csv-enrichment__status-progress-track">
            <div
              className="bs-csv-enrichment__status-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Enriched Data section */}
      <div className="bs-csv-enrichment__data-section">
        <div className="bs-csv-enrichment__data-section-header">
          <div className="enriched-data-header">
            <span className="enriched-data">
              {isLoading ? (
                <SkeletonLoading width={140} height={22} />
              ) : (
                'Enriched Data'
              )}
            </span>
            <span className="enriched-row">
              {isLoading ? (
                <SkeletonLoading width={100} height={18} />
              ) : (
                `${recordsJob?.enrichedRecords} rows enriched`
              )}
            </span>
          </div>
          {(typeChip || revealChip) && (
            <div className="d-flex align-items-center gap-8">
              {isLoading ? (
                <>
                  <SkeletonLoading width={100} height={32} borderRadius={4} />
                  <SkeletonLoading width={100} height={32} borderRadius={4} />
                </>
              ) : (
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '6px' }}
                >
                  <span className="regular-1 gray-txt-15">
                    Enrichment Type:
                  </span>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: '6px' }}
                  >
                    {typeChip && (
                      <span className="job-type-chip regular-1 semibold-2 gray-txt-11">
                        {renderTypeChipIcon()}
                        <span>{typeChip}</span>
                      </span>
                    )}
                    {revealChip &&
                      enrichmentColumnType === EnrichmentType.PEOPLE && (
                        <>
                          <ChevronRight
                            width={12}
                            height={12}
                            color="#6b7280"
                          />
                          <span className="reveal-type-chip regular-1 semibold-2 gray-txt-11">
                            {renderRevealChipIcon()}
                            <span>{revealChip}</span>
                          </span>
                        </>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {renderEnrichedRecordsBlock()}
      </div>
    </div>
  );
};

export default EnrichmentStatusPage;
